/* eslint-disable no-nested-ternary */
// eslint-disable-next-line import/no-extraneous-dependencies
import shortid from 'shortid';
import moment from 'moment';

import { useSelector } from 'react-redux';

import _ from '../../../utils/lodash';
import { ragStatusColors } from '../../../settings/other';
import { TreatmentResponseGroup } from '../../../enums';
import globals from '../../../globals/communityGlobals';

const locale = globals.getArea('dailyPlanning');

export const mapTreatmentInstances = treatmentInstances => {
  treatmentInstances.forEach(treatmentInstance => {
    treatmentInstance.activities = treatmentInstance.treatmentInstanceActivities
      .map(activity => activity.name)
      .join(', ');
  });

  return treatmentInstances;
};

export const mapPlanningDetails = response => {
  const clinicianFilterPhrase = useSelector(state => state.areas.dailyPlanning.filters.clinician) || '';
  const bandFilter = useSelector(state => state.areas.dailyPlanning.filters.grades) || '';

  const mappedResult = {
    dataItems: [],
    period: null,
    summaryHoursStats: null,
    clinicians: [],
    clinicianBands: [],
    isMileageCapEnabled: false,
    treatmentInstances: [],
  };

  if (response) {
    const data = _.filter(
      response.clinicianPlanningDetails,
      x =>
        clinicianFilterPhrase === '' ||
        x.clinician.name.toLowerCase().indexOf(clinicianFilterPhrase) !== -1 ||
        x.clinician.staffNumber.startsWith(clinicianFilterPhrase),
    );

    let filteredData = [...data];

    if (bandFilter.length > 0 && bandFilter[0] !== '')
      filteredData = _.filter(data, x => bandFilter.includes(x.clinician.bandId));

    const clinicianBands = _.sortBy(_.map(data, f => ({ id: f.clinician.bandId, name: f.clinician.band })), ['name']);
    const extraInfoStatus = clinician => {
      if (clinician.differentOrder) return locale.getMsg('differentOrder');
      if (clinician.isLate) return `${clinician.lateDuration} mins`;
      if (clinician.isAhead) return `${clinician.aheadDuration} mins`;
      return 'On Time';
    };

    // Mapping into view model
    mappedResult.isAutoallocateInProgress = response.isAutoallocateInProgress;
    mappedResult.period = response.period;
    mappedResult.isMileageCapEnabled = response.isMileageCapEnabled;
    mappedResult.summaryHoursStats = response.summaryHoursStats;
    mappedResult.treatmentInstances = mapTreatmentInstances(response.treatmentInstances);
    mappedResult.clinicians = _.map(filteredData, f => ({ id: f.clinician.id, name: f.clinician.name }));
    mappedResult.clinicianBands = _.uniqBy(clinicianBands, 'id');
    mappedResult.unfilteredClinicians = _.map(response.clinicianPlanningDetails, f => ({
      id: f.clinician.id,
      name: f.clinician.name,
      availabilities: f.clinician.availabilities.map(a => ({ ...a, index: shortid.generate() })),
    }));

    _.each(filteredData, item => {
      mappedResult.dataItems.push({
        cardDetails: {
          index: shortid.generate(),
          id: item.clinician.id,
          postingId: item.clinician.postingId,
          cardRagStatus: item.clinician.ragStatus.name.toLowerCase(),
          cardRagStatusId: item.clinician.ragStatus.value,
          personName: item.clinician.name,
          bandName: item.clinician.band,
          showExtraInfo: item.clinician.showExtraInfo,
          desc: item.clinician.showExtraInfo ? extraInfoStatus(item.clinician) : '',
          descColor: item.clinician.differentOrder
            ? ragStatusColors.amber
            : item.clinician.isLate
            ? ragStatusColors.red
            : ragStatusColors.green,
          hasMileageCap: item.clinician.hasMileageCap,
          isOnline: item.clinician.isOnline,
          gradeEquivalence: item.clinician.gradeEquivalence,
        },
        activities: {
          clinicianId: item.clinician.id,
          postingId: item.clinician.postingId,
          availabilities: item.clinician.availabilities.map(a => ({ ...a, index: shortid.generate() })),
          assignments: item.assignments.map(a => ({ ...a, index: shortid.generate() })),
        },
        source: item.clinician, // add here source item like ... item.clinician,
      });
    });
  }

  return mappedResult;
};

export const mapClinicianTreatments = response => {
  if (response === null) return null;

  let mappedTreatmentGroups = [
    {
      name: 'BestMatch',
      text: 'Best Match',
      isAssignable: true,
      items: [],
      children: [TreatmentResponseGroup.BestMatch],
    },
    {
      name: 'OutsideOfMileageCap',
      text: 'Outside Of Mileage Cap',
      isAssignable: true,
      items: [],
      children: [TreatmentResponseGroup.OutsideOfMilleageCap],
    },
    {
      name: 'PartialMatch',
      text: 'Partial Match',
      isAssignable: false,
      items: [],
      children: [TreatmentResponseGroup.MissingAvailability, TreatmentResponseGroup.TimeSlotsAlreadyOccupied],
    },
    {
      name: 'NoMatch',
      text: 'No Match',
      isAssignable: false,
      items: [],
      children: [
        TreatmentResponseGroup.MissingGrade,
        TreatmentResponseGroup.MissingSkill,
        TreatmentResponseGroup.ExcludedClinician,
      ],
    },
  ];

  if (!response.isMileageCapEnabled)
    mappedTreatmentGroups = mappedTreatmentGroups.filter(tg => tg.name !== 'OutsideOfMilleageCap');

  const mappedResult = mappedTreatmentGroups.map(group => {
    response.treatmentGroups.forEach(x => {
      if (group.children.includes(x.name)) {
        group.items = [...group.items, ...x.items];
      }
    });

    mapTreatmentInstances(group.items);
    return group;
  });

  return mappedResult;
};

export const mapAvailableTimeSpans = response => {
  return response ? response.clinicianTimeSpans : null;
};

export const mapOptions = response => {
  return response ? response.options : null;
};

export const mapMapViewDetails = response => {
  const bandFilter = useSelector(state => state.areas.dailyPlanning.filters.grades) || '';

  const mappedResult = {
    data: [],
    clinicianBands: [],
    isMileageCapEnabled: false,
  };

  if (response) {
    let filteredData = response.clinicianPlanningDetails;

    mappedResult.isMileageCapEnabled = response.isMileageCapEnabled;

    if (bandFilter.length > 0 && bandFilter[0] !== '')
      filteredData = _.filter(response.clinicianPlanningDetails, x => bandFilter.includes(x.clinician.bandId));

    const clinicianBands = _.sortBy(
      _.map(response.clinicianPlanningDetails, f => ({ id: f.clinician.bandId, name: f.clinician.band })),
      ['name'],
    );
    mappedResult.clinicianBands = _.uniqBy(clinicianBands, 'id');

    _.each(filteredData, item => {
      mappedResult.data.push({
        clinician: {
          index: shortid.generate(),
          id: item.clinician.id,
          postingId: item.clinician.postingId,
          ragStatus: item.clinician.ragStatus,
          cardRagStatusId: item.clinician.ragStatus.value,
          personName: item.clinician.name,
          band: item.clinician.band,
          showExtraInfo: item.clinician.showExtraInfo,
          hasValidBasePostCode: item.clinician.hasValidBasePostCode,
          homePostCode: item.clinician.homePostCode,
          mileageCap: item.clinician.mileageCap,
          desc: item.clinician.showExtraInfo
            ? item.clinician.isLate
              ? `${item.clinician.lateDuration} mins`
              : 'On Time'
            : '',
          descColor: item.clinician.isLate ? ragStatusColors.red : ragStatusColors.green,
          hasMileageCap: item.clinician.hasMileageCap,
          isOnline: item.clinician.isOnline,
          assignedVisitsCount: item.clinician.assignedVisitsCount,
          completedVisitsCount: item.clinician.completedVisitsCount,
          clinicianLocation: item.clinician.clinicianLocation,
          clinicianColor: item.clinician.clinicianColor,
          staffNumber: item.clinician.staffNumber,
          availabilities: item.clinician.availabilities,
        },
        assignments: item.assignments,
      });
    });
  }

  return response ? mappedResult : null;
};

export const mapClinicianDetails = response => {
  if (response === null) return null;
  const mappedResult = { ...response };
  mappedResult.weeklyAvailabilities = [];
  _.each(response.datesOfWeek, item => {
    mappedResult.weeklyAvailabilities.push({
      key: item,
      value: response.weeklyAvailabilities.filter(i => moment(i.startDate).isSame(item, 'day')),
    });
  });

  return mappedResult;
};

export const mapNonPatientActivityDetails = response => {
  return response ? response.nonPatientActivity : null;
};
